import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, relTime, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";

/** Needs Attention (admin spec §6): one prioritised queue with severity, SLA and one primary action. */

export interface AlertItem {
  id: string;
  kind: string;
  severity: "critical" | "high" | "medium";
  title: string;
  detail: string | null;
  refType: string;
  refId: string;
  churchId: string | null;
  createdAt: string;
  dueAt: string;
  overdue: boolean;
  action: "care" | "questions" | "moderation" | "people" | "fams" | "prayer" | "churches" | "rewards" | "decisions" | "ops";
}

export interface AttentionData {
  items: AlertItem[];
  counts: { critical: number; high: number; medium: number; overdue: number };
}

export function useAttention(refresh = false) {
  const scope = useScope();
  return useQuery({
    queryKey: ["admin", "attention", scope.churchId, refresh],
    queryFn: () => api<AttentionData>(`/admin/attention${scopeQuery(scope, "church", refresh ? { refresh: "1" } : {})}`),
    refetchInterval: 60_000,
  });
}

export function AdminAttention() {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const q = useAttention();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "attention"] });
  const resolve = useMutation({ mutationFn: (id: string) => api(`/admin/attention/${id}/resolve`, { method: "POST" }), onSuccess: invalidate });
  const care = useMutation({ mutationFn: (p: { id: string; status: "in_contact" | "closed" }) => api(`/admin/care/${p.id}`, { method: "POST", json: { status: p.status } }), onSuccess: invalidate });
  const refresh = useMutation({ mutationFn: () => api(`/admin/attention?refresh=1`), onSuccess: invalidate });

  const d = q.data;
  return (
    <>
      <h1>{t("admin.attention.title")}</h1>
      <p className="muted">{t("admin.attention.body")}</p>
      <div className="toolbar">
        <div className="chips">
          <span className="pill critical">
            {d?.counts.critical ?? "–"} {t("admin.attention.critical")}
          </span>
          <span className="pill high">
            {d?.counts.high ?? "–"} {t("admin.attention.high")}
          </span>
          <span className="pill">
            {d?.counts.medium ?? "–"} {t("admin.attention.medium")}
          </span>
          <span className="pill warn">
            {d?.counts.overdue ?? "–"} {t("admin.attention.overdue")}
          </span>
        </div>
        <button className="btn small ghost" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {t("admin.attention.refresh")}
        </button>
      </div>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      {d?.items.length === 0 ? <p className="muted empty">{t("admin.attention.empty")}</p> : null}
      <div className="queue">
        {d?.items.map((a) => (
          <div key={a.id} className={`queue-item sev-${a.severity} ${a.overdue ? "overdue" : ""}`}>
            <div className="sev">{t(`admin.attention.${a.severity}`)}</div>
            <div className="grow">
              <div className="kind">{t(`admin.attention.kind.${a.kind}`)}</div>
              <strong>{a.title}</strong>
              {a.detail ? <div className="muted">{a.detail}</div> : null}
              <div className="muted small">
                {t("admin.attention.since", { time: relTime(a.createdAt, locale) })} · {a.overdue ? <span className="error">{t("admin.attention.overdue")}</span> : t("admin.attention.dueIn", { time: relTime(a.dueAt, locale) })}
              </div>
            </div>
            <div className="actions">
              <PrimaryAction a={a} onCare={(status) => care.mutate({ id: a.refId, status })} />
              <button className="btn small ghost" onClick={() => resolve.mutate(a.id)}>
                {t("admin.attention.resolve")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PrimaryAction({ a, onCare }: { a: AlertItem; onCare: (status: "in_contact" | "closed") => void }) {
  const { t } = useT();
  switch (a.action) {
    case "care":
      return (
        <button className="btn small" onClick={() => onCare("in_contact")}>
          {t("admin.attention.takeCare")}
        </button>
      );
    case "questions":
      return (
        <Link className="btn small" to="/admin/questions">
          {t("admin.attention.action.questions")}
        </Link>
      );
    case "moderation":
      return (
        <Link className="btn small" to="/admin/moderation">
          {t("admin.attention.action.moderation")}
        </Link>
      );
    case "prayer":
      return (
        <Link className="btn small" to="/admin/prayer">
          {t("admin.attention.action.prayer")}
        </Link>
      );
    case "churches":
      return (
        <Link className="btn small" to="/admin/churches">
          {t("admin.attention.action.churches")}
        </Link>
      );
    case "people":
      return (
        <Link className="btn small" to="/admin/people">
          {t("admin.attention.action.people")}
        </Link>
      );
    case "rewards":
      return (
        <Link className="btn small" to="/admin/rewards">
          {t("admin.attention.action.rewards")}
        </Link>
      );
    case "decisions":
      return (
        <Link className="btn small" to="/admin/decisions">
          {t("admin.attention.action.decisions")}
        </Link>
      );
    case "ops":
      return (
        <a className="btn small" href={`https://status.${location.hostname.replace(/^admin\./, "")}`} target="_blank" rel="noreferrer">
          {t("admin.attention.action.ops")}
        </a>
      );
    case "fams":
      return (
        <Link className="btn small" to="/admin/fams">
          {t("admin.attention.action.fams")}
        </Link>
      );
  }
}
